import React, { useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

const HelpButton = ({ 
  helpFile, 
  title = "Help", 
  size = 20, 
  className = "",
  showTooltip = true,
  keyboardShortcut = true,
  type = "page", // "page" or "global"
  variant = "button", // "button", "icon", "text"
  openInNewWindow = true,
  style = {}
}) => {
  
  const openHelp = () => {
    let helpUrl;
    
    if (type === "global") {
      // Open Global Help Center
      helpUrl = '/help';
    } else if (helpFile) {
      // Open specific help file
      helpUrl = `/help/${helpFile}`;
    } else {
      console.warn('No help file specified for HelpButton');
      return;
    }

    // Open help in new window
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );

    // Focus the new window
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Keyboard shortcut handler
  useEffect(() => {
    if (!keyboardShortcut) return;

    const handleKeyPress = (event) => {
      // Check if ? key is pressed and no input/textarea is focused
      if (event.key === '?' && !['INPUT', 'TEXTAREA'].includes(event.target.tagName)) {
        event.preventDefault();
        openHelp();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [keyboardShortcut, helpFile, type]);

  const getButtonStyles = () => {
    const baseStyles = {
      border: 'none',
      borderRadius: '0',
      backgroundColor: 'transparent',
      color: '#6c757d',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      cursor: 'pointer'
    };

    switch (variant) {
      case 'icon':
        return {
          ...baseStyles,
          padding: '8px',
          border: '1px solid #dee2e6'
        };
      case 'text':
        return {
          ...baseStyles,
          padding: '4px 8px',
          border: '1px solid #dee2e6'
        };
      default: // button
        return {
          ...baseStyles,
          padding: '4px 8px',
          border: '1px solid #dee2e6'
        };
    }
  };

  const getHoverStyles = () => {
    return {
      backgroundColor: '#f8f9fa',
      borderColor: '#007B8F',
      color: '#007B8F'
    };
  };

  const getLeaveStyles = () => {
    return {
      backgroundColor: 'transparent',
      borderColor: variant === 'icon' ? '#dee2e6' : '#dee2e6',
      color: '#6c757d'
    };
  };

  // If using cam-help-center-btn class, don't apply inline styles
  const useCustomStyles = className.includes('cam-help-center-btn');
  const buttonStyles = useCustomStyles ? style : {...getButtonStyles(), ...style};
  
  return (
    <button
      onClick={openHelp}
      className={`help-button ${className}`}
      title={showTooltip ? `${title} (Press ? for keyboard shortcut)` : title}
      style={buttonStyles}
      onMouseEnter={(e) => {
        if (!useCustomStyles) {
          Object.assign(e.target.style, {...getHoverStyles(), ...style});
        }
      }}
      onMouseLeave={(e) => {
        if (!useCustomStyles) {
          Object.assign(e.target.style, {...getLeaveStyles(), ...style});
        }
      }}
    >
      <HelpCircle size={size} />
      {(variant === 'text' || variant === 'button') && title !== "Help" && (
        <span style={{ fontSize: '12px', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
          {title}
        </span>
      )}
    </button>
  );
};

export default HelpButton;